import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { ReactComponent as AnalyzeIcon } from "Assets/icons/menu/analytics.svg";
import { ReactComponent as OptimizeIcon } from "Assets/icons/menu/optimization.svg";
import { ReactComponent as AiIcon } from "Assets/icons/menu/ai.svg";
import { ReactComponent as ReportIcon } from "Assets/icons/menu/reporting.svg";
import { ReactComponent as SettingsIcon } from "Assets/icons/menu/connect.svg";
import { ReactComponent as SuperAdminIcon } from "Assets/icons/menu/super-admin.svg";
import { sortByDate } from "Helpers/arrays";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import FlyoutMenuComponent from "./FlyoutMenu";
import useProtectedRoutes, { IProtectedRoute } from "Hooks/useProtectedRoutes";
import { RouteNames } from "Hooks/routeNames";
import { useSelectedProject } from "Hooks";
import { useUser } from "Hooks/useUser";
import { ApiACLRole, ApiProjectType, ApiReportResponse } from "@incendium/api";
import { useDispatch, useSelector } from "react-redux";
import { leftMenuSelector } from "Selectors/leftMenuSelector";
import {
  toggleFlyoutPinned,
  toggleSecondaryAvailable,
} from "Actions/LeftMenuActions/leftMenuActions";
import { LEFT_BAR_WIDTH, TOP_BAR_HEIGHT } from "../constants";
import AccessLevel from "Components/AccessLevel/AccessLevel";
import { useMount } from "react-use";
import { closeSecondaryMenu } from "Actions";
import useProjectSettings from "features/project/hooks/useProjectSettings";
import { useAISessions } from "features/ai/hooks/useAISessions";
import { useAiContext } from "features/ai/providers/AiProvider";
import { deleteAISession } from "features/ai/services/aiService";

type RoutesByNameMap = ReturnType<typeof useProtectedRoutes>["routesByName"];

const SideMenuContainer = styled(Box)(({ theme }) => ({
  width: LEFT_BAR_WIDTH,
  height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
  backgroundColor: "#e5ebfe",
  display: "flex",
  flexDirection: "column",
  boxShadow: "inset 2px 0 5px rgba(0, 0, 0, 0.2)",
  position: "relative",
}));

const TopSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  alignItems: "center",
  paddingTop: theme.spacing(2),
}));

const BottomSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingBottom: theme.spacing(2),
}));

const MenuButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isActive",
})<{ $isActive: boolean }>(({ theme, $isActive }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  cursor: "pointer",
  padding: "12px 8px",
  transition: "all 0.2s ease",
  position: "relative",
  width: "100%",
  color: $isActive ? theme.palette.primary.main : theme.palette.secondary.main,
  borderRadius: 0,
  "&:hover": {
    color: theme.palette.primary.main,
    "& svg": {
      fill: theme.palette.primary.main,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      right: -2,
      width: 2,
      height: "100%",
    },
  },
  "& svg": {
    height: 25,
    width: 25,
    fill: $isActive ? theme.palette.primary.main : theme.palette.secondary.main,
  },
}));

const ButtonText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 500,
  textAlign: "center",
  color: "inherit",
}));

const ButtonWithFlyout = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  height: "fit-content",
  width: "100%",
}));

interface IMenuDataItem {
  text: string;
  path: string;
  route: IProtectedRoute;
  action?: () => void;
  deleteAction?: (id: number | string) => void;
  id?: number | string;
}

export interface IMenuData {
  title: string;
  items: IMenuDataItem[];
  isBeta?: boolean;
  variant?: "section" | "group" | "divider";
}

function buildSuperAdminMenuSections(
  pathByName: (
    name: RouteNames,
    opts?: Record<string, number | string | undefined>,
  ) => string,
  routesByName: RoutesByNameMap,
): IMenuData[] {
  return [
    {
      title: "Subscriptions",
      items: [
        {
          text: "Subscriptions",
          path: pathByName(RouteNames.SUBSCRIPTIONS),
          route: routesByName[RouteNames.SUBSCRIPTIONS],
        },
        {
          text: "Contracts",
          path: pathByName(RouteNames.CONTRACTS),
          route: routesByName[RouteNames.CONTRACTS],
        },
        {
          text: "Products",
          path: pathByName(RouteNames.PRODUCTS),
          route: routesByName[RouteNames.PRODUCTS],
        },
        {
          text: "Project Subscriptions",
          path: pathByName(RouteNames.PROJECT_SUBSCRIPTIONS),
          route: routesByName[RouteNames.PROJECT_SUBSCRIPTIONS],
        },
      ],
    },
    {
      title: "Users",
      items: [
        {
          text: "Users",
          path: pathByName(RouteNames.USERS),
          route: routesByName[RouteNames.USERS],
        },
        {
          text: "SSO Tenants",
          path: pathByName(RouteNames.SSO_TENANTS),
          route: routesByName[RouteNames.SSO_TENANTS],
        },
        {
          text: "Onboarding",
          path: pathByName(RouteNames.ONBOARDING),
          route: routesByName[RouteNames.ONBOARDING],
        },
        {
          text: "New Project",
          path: pathByName(RouteNames.NEW_PROJECT),
          route: routesByName[RouteNames.NEW_PROJECT],
        },
      ],
    },
    {
      title: "Analytics Labels",
      items: [
        {
          text: "Analytics Labels",
          path: pathByName(RouteNames.ANALYTICS_LABELS),
          route: routesByName[RouteNames.ANALYTICS_LABELS],
        },
      ] as IMenuDataItem[],
    },
    {
      title: "AI",
      items: [
        {
          text: "AI Credits",
          path: pathByName(RouteNames.AI_CREDITS),
          route: routesByName[RouteNames.AI_CREDITS],
        },
      ] as IMenuDataItem[],
    },
    {
      title: "Component Blocks",
      items: [
        {
          text: "Shared Component Blocks",
          path: pathByName(RouteNames.SHARED_COMPONENT_BLOCKS),
          route: routesByName[RouteNames.SHARED_COMPONENT_BLOCKS],
        },
      ] as IMenuDataItem[],
    },
  ];
}

interface ISideMenuProps {
  reports: ApiReportResponse[];
  reportsFetched: boolean;
  mouseOver: boolean;
}

function SideMenu({ reports, reportsFetched, mouseOver }: ISideMenuProps) {
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [clickedMenuButton, setClickedMenuButton] = useState<string | null>(
    null,
  );
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const dispatch = useDispatch();
  const {
    isFlyoutPinned: isFlyoutPinnedOnly,
    isSecondaryAvailable,
    isSecondaryOpen,
  } = useSelector(leftMenuSelector);
  const { pathByName, routesByName } = useProtectedRoutes();
  const { selectedProject, selectedClient } = useSelectedProject();
  const { hasProjectType } = useProjectSettings();
  const { hasPermission } = useUser();
  const location = useLocation();
  const { active, setActive, loadSession } = useAiContext();

  const favReports = useMemo(
    () => reports.filter((r) => r.favourite),
    [reports],
  );
  const isFlyoutPinned = isFlyoutPinnedOnly || isSecondaryAvailable;
  const { aiSessions, setAiSessions, fetched } = useAISessions();
  const history = useHistory();
  const prevLocationRef = useRef(location.pathname);
  const activeRef = useRef(active);

  // Keep ref in sync with active state
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useMount(() => {
    if (isSecondaryOpen) {
      dispatch(closeSecondaryMenu());
    }
  });

  // on route change, close the ai chat
  useEffect(() => {
    // Only close if location actually changed (not just on mount)
    if (prevLocationRef.current !== location.pathname) {
      prevLocationRef.current = location.pathname;
      // Read current active value from ref to avoid stale closure
      if (activeRef.current) {
        setActive(false);
      }
    }
  }, [location.pathname, setActive]);

  useEffect(() => {
    if (!isFlyoutPinned) {
      setClickedMenuButton(null);
    }
  }, [isFlyoutPinned]);

  useEffect(() => {
    if (
      isSecondaryOpen &&
      !matchPath(location.pathname, {
        path: "/clients/:clientId/projects/:projectId/analyse/reports/:reportId",
        exact: true,
        strict: false,
      }) &&
      !matchPath(location.pathname, {
        path: "/clients/:clientId/projects/:projectId/publish/components/:componentId",
        exact: true,
        strict: false,
      })
    ) {
      dispatch(closeSecondaryMenu());
    }
    if (
      isSecondaryAvailable &&
      !matchPath(location.pathname, {
        path: "/clients/:clientId/projects/:projectId/analyse/reports/:reportId",
        exact: true,
        strict: false,
      }) &&
      !matchPath(location.pathname, {
        path: "/clients/:clientId/projects/:projectId/publish/components/:componentId",
        exact: true,
        strict: false,
      })
    ) {
      dispatch(toggleSecondaryAvailable());
    }
  }, [location, dispatch, isSecondaryOpen, isSecondaryAvailable]);

  const handleMouseEnter = (buttonName: string) => {
    setHoveredButton(buttonName);
    if (!isFlyoutPinned) {
      setIsFlyoutOpen(true);
    }
  };

  const handleMouseLeave = useCallback(() => {
    if (!isFlyoutPinned) {
      setIsFlyoutOpen(false);
    }
  }, [isFlyoutPinned]);

  const handleMenuButtonClick = (buttonName: string) => {
    if (!isFlyoutPinned) {
      return;
    }
    setClickedMenuButton(clickedMenuButton === buttonName ? null : buttonName);
    setHoveredButton(null);
  };

  useEffect(() => {
    if (!mouseOver) {
      setHoveredButton(null);
    }
  }, [mouseOver]);

  const handlePinToggle = () => {
    dispatch(toggleFlyoutPinned());
  };

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      if (!selectedProject?.id) return;
      try {
        await deleteAISession(selectedProject.id as number, sessionId);
        // Remove the session from the list using functional update to avoid stale closure
        setAiSessions((prevSessions) =>
          prevSessions.filter((s) => s.id !== sessionId),
        );
      } catch (error) {
        console.error("Failed to delete session:", error);
      }
    },
    [selectedProject?.id, setAiSessions],
  );

  const isGetStarted = matchPath(location.pathname, {
    path: "/clients/:clientId/projects/:projectId/get-started",
    exact: true,
    strict: false,
  });

  const menuData = useMemo(() => {
    const superAdminSections = buildSuperAdminMenuSections(
      pathByName,
      routesByName,
    );

    if (!selectedClient || !selectedProject) {
      if (!hasPermission(ApiACLRole.SUPER_ADMIN)) {
        return {
          Analyze: [],
          Optimize: [],
          Report: [],
          Ai: [],
          Settings: [],
          SuperAdmin: [],
        };
      }
      return {
        Analyze: [],
        Optimize: [],
        Report: [],
        Ai: [],
        Settings: [],
        SuperAdmin: superAdminSections,
      };
    }

    const menu: {
      Analyze: IMenuData[];
      Optimize: IMenuData[];
      Report: IMenuData[];
      Ai: IMenuData[];
      Settings: IMenuData[];
      SuperAdmin: IMenuData[];
    } = {
      Analyze: [
        ...(hasProjectType(ApiProjectType.TYPE_ECOMMERCE)
          ? [
              {
                title: "Store Intelligence",
                variant: "section" as const,
                items: [
                  {
                    text: "Overview",
                    path: pathByName(RouteNames.STORE_OVERVIEW, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.STORE_OVERVIEW],
                  },
                  {
                    text: "Product Performance",
                    path: pathByName(RouteNames.STORE_PRODUCT_PERFORMANCE, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.STORE_PRODUCT_PERFORMANCE],
                  },
                  {
                    text: "Customers & Retention",
                    path: pathByName(RouteNames.STORE_CUSTOMER_RETENTION, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.STORE_CUSTOMER_RETENTION],
                  },
                ],
              },
              {
                title: "",
                variant: "divider" as const,
                items: [],
              },
              // {
              //   title: "Marketing & Engagement",
              //   variant: "group" as const,
              //   items: [],
              // },
            ]
          : []),
        {
          title: "Quick Insights",
          items: [
            ...(hasProjectType(ApiProjectType.TYPE_ECOMMERCE)
              ? [
                  {
                    text: "Ecommerce",
                    path: pathByName(RouteNames.ECOM_INSIGHTS, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                      insight: "overview",
                    }),
                    route: routesByName[RouteNames.ECOM_INSIGHTS],
                  },
                ]
              : []),
            {
              text: "Site Overview",
              path: pathByName(RouteNames.SITE_OVERVIEW, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.SITE_OVERVIEW],
            },
            {
              text: "Traffic & Campaigns",
              path: pathByName(RouteNames.TRAFFIC_CAMPAIGNS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.TRAFFIC_CAMPAIGNS],
            },
            {
              text: "Landing Pages",
              path: pathByName(RouteNames.LANDING_PAGES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LANDING_PAGES],
            },
            {
              text: "Devices",
              path: pathByName(RouteNames.DEVICES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.DEVICES],
            },
            {
              text: "Time and Day",
              path: pathByName(RouteNames.TIME_AND_DAY, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.TIME_AND_DAY],
            },
          ],
        },
        {
          title: "Ad Optimization",
          items: [
            {
              text: "Campaign Drilldown",
              path: pathByName(RouteNames.AD_PERFORMANCE_ANALYSIS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.AD_PERFORMANCE_ANALYSIS],
            },
            {
              text: "Ads",
              path: pathByName(RouteNames.ADS_OVERVIEW, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.ADS_OVERVIEW],
            },
            {
              text: "Performance Overview",
              path: pathByName(RouteNames.PAID_MEDIA, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
                insight: "platforms",
              }),
              route: routesByName[RouteNames.PAID_MEDIA],
            },

            {
              text: "Keywords",
              path: pathByName(RouteNames.PAID_MEDIA, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
                insight: "keywords",
              }),
              route: routesByName[RouteNames.PAID_MEDIA],
            },
            {
              text: "Time and Day",
              path: pathByName(RouteNames.AD_TIME_AND_DAY, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.AD_TIME_AND_DAY],
            },
          ],
        },
        {
          title: "Explore",
          items: [
            {
              text: "Attribution Explorer",
              path: pathByName(RouteNames.ATTRIBUTION_EXPLORER, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.ATTRIBUTION_EXPLORER],
            },
            {
              text: "Metric Explorer",
              path: pathByName(RouteNames.METRIC_EXPLORER, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.METRIC_EXPLORER],
            },
            {
              text: "Dimension Explorer",
              path: pathByName(RouteNames.DIMENSION_EXPLORER, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.DIMENSION_EXPLORER],
            },
            {
              text: "UTM Explorer",
              path: pathByName(RouteNames.UTM_EXPLORER, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.UTM_EXPLORER],
            },
          ],
        },
        {
          title: "Organic & Owned",
          items: [
            {
              text: "Organic Search",
              path: pathByName(RouteNames.ORGANIC, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
                insight: "overview",
              }),
              route: routesByName[RouteNames.ORGANIC],
            },
            {
              text: "Email Marketing",
              path: pathByName(RouteNames.EMAIL_PERFORMANCE, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.EMAIL_PERFORMANCE],
            },
            {
              text: "Organic Social",
              path: pathByName(RouteNames.ORGANIC_SOCIAL, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.ORGANIC_SOCIAL],
            },
            {
              text: "Referral Traffic",
              path: pathByName(RouteNames.REFERRAL_TRAFFIC, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.REFERRAL_TRAFFIC],
            },
          ],
        },
        {
          title: "Content",
          items: [
            {
              text: "Content Performance Analysis",
              path: pathByName(RouteNames.CONTENT_PERFORMANCE_ANALYSIS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.CONTENT_PERFORMANCE_ANALYSIS],
            },
            {
              text: "Locations Overview",
              path: pathByName(RouteNames.LOCATIONS_OVERVIEW, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LOCATIONS_OVERVIEW],
            },
            {
              text: "User Journey Explorer",
              path: pathByName(RouteNames.JOURNEY_EXPLORER, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.JOURNEY_EXPLORER],
            },
            {
              text: "Exit Pages",
              path: pathByName(RouteNames.EXIT_PAGES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.EXIT_PAGES],
            },
            {
              text: "Error Pages",
              path: pathByName(RouteNames.ERROR_PAGES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.ERROR_PAGES],
            },
          ],
        },

        ...(hasProjectType(ApiProjectType.TYPE_ECOMMERCE)
          ? [
              {
                title: "Products",
                items: [
                  {
                    text: "Overview",
                    path: pathByName(RouteNames.PRODUCTS_OVERVIEW, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.PRODUCTS_OVERVIEW],
                  },
                  {
                    text: "Deep Dive",
                    path: pathByName(RouteNames.PRODUCT_DEEP_DIVE, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.PRODUCT_DEEP_DIVE],
                  },
                  {
                    text: "Category Deep Dive",
                    path: pathByName(RouteNames.PRODUCT_CATEGORY_DEEP_DIVE, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.PRODUCT_CATEGORY_DEEP_DIVE],
                  },
                  {
                    text: "Performance Map",
                    path: pathByName(RouteNames.PRODUCTS_PERFORMANCE_MAP, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.PRODUCTS_PERFORMANCE_MAP],
                  },
                  {
                    text: "Product Performance Analysis",
                    path: pathByName(RouteNames.PRODUCT_PERFORMANCE_ANALYSIS, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route:
                      routesByName[RouteNames.PRODUCT_PERFORMANCE_ANALYSIS],
                  },
                ],
              },
            ]
          : hasProjectType(ApiProjectType.TYPE_RECRUITMENT)
            ? [
                {
                  title: "Jobs",
                  items: [
                    {
                      text: "Overview",
                      path: pathByName(RouteNames.JOBS_OVERVIEW, {
                        clientId: selectedClient?.id,
                        projectId: selectedProject?.id,
                      }),
                      route: routesByName[RouteNames.JOBS_OVERVIEW],
                    },
                    {
                      text: "Deep Dive",
                      path: pathByName(RouteNames.JOBS_DEEP_DIVE, {
                        clientId: selectedClient?.id,
                        projectId: selectedProject?.id,
                      }),
                      route: routesByName[RouteNames.JOBS_DEEP_DIVE],
                    },
                    {
                      text: "Category Deep Dive",
                      path: pathByName(RouteNames.JOBS_CATEGORY_DEEP_DIVE, {
                        clientId: selectedClient?.id,
                        projectId: selectedProject?.id,
                      }),
                      route: routesByName[RouteNames.JOBS_CATEGORY_DEEP_DIVE],
                    },
                    {
                      text: "Performance Map",
                      path: pathByName(RouteNames.JOBS_PERFORMANCE_MAP, {
                        clientId: selectedClient?.id,
                        projectId: selectedProject?.id,
                      }),
                      route: routesByName[RouteNames.JOBS_PERFORMANCE_MAP],
                    },
                  ],
                },
              ]
            : []),
        {
          title: "Audiences",
          items: [
            {
              text: "User Dashboard",
              path: pathByName(RouteNames.USER_DASHBOARD, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.USER_DASHBOARD],
            },
            {
              text: "Funnel Analysis",
              path: pathByName(RouteNames.FUNNEL_ANALYSIS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.FUNNEL_ANALYSIS],
            },
            {
              text: "New & Returning",
              path: pathByName(RouteNames.AUDIENCE_NEW_RETURNING, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
                insight: "new_user acquisition",
              }),
              route: routesByName[RouteNames.AUDIENCE_NEW_RETURNING],
            },
            {
              text: "User Journey",
              path: pathByName(RouteNames.AUDIENCE_USER_JOURNEY, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
                insight: "overview",
              }),
              route: routesByName[RouteNames.AUDIENCE_USER_JOURNEY],
            },
            {
              text: "Geo Explorer",
              path: pathByName(RouteNames.GEO_EXPLORER, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.GEO_EXPLORER],
            },
            {
              text: "Audience Comparison",
              path: pathByName(RouteNames.AUDIENCE_COMPARISON, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
                insight: "overview",
              }),
              route: routesByName[RouteNames.AUDIENCE_COMPARISON],
            },
            {
              text: "Audience Deep Dive",
              path: pathByName(RouteNames.AUDIENCE_DEEP_DIVE, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.AUDIENCE_DEEP_DIVE],
            },
            {
              text: "LTV Analysis",
              path: pathByName(RouteNames.CUSTOMER_VALUE_COHORT, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.CUSTOMER_VALUE_COHORT],
            },
          ],
        },
      ],
      Optimize: [
        {
          title: "Landing Pages",
          items: [
            {
              text: "Themes",
              path: pathByName(RouteNames.LIGHTNING_LANDING_THEME, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LIGHTNING_LANDING_THEME],
            },
            {
              text: "Components",
              path: pathByName(RouteNames.LIGHTNING_LANDING_COMPONENTS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LIGHTNING_LANDING_COMPONENTS],
            },
            {
              text: "Blocks",
              path: pathByName(RouteNames.LIGHTNING_LANDING_BLOCKS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LIGHTNING_LANDING_BLOCKS],
            },
            {
              text: "Templates",
              path: pathByName(RouteNames.LIGHTNING_LANDING_TEMPLATES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LIGHTNING_LANDING_TEMPLATES],
            },
            {
              text: "Pages",
              path: pathByName(RouteNames.LIGHTNING_LANDING_PAGES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LIGHTNING_LANDING_PAGES],
            },
            {
              text: "Insights",
              path: pathByName(RouteNames.LIGHTNING_LANDING_PAGES_REPORT, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LIGHTNING_LANDING_PAGES_REPORT],
            },
          ],
          // isBeta: true
        },
        {
          title: "Rezonate",
          items: [
            {
              text: "Meta",
              path: pathByName(RouteNames.REZONATE_META_LANDING_PAGES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.REZONATE_META_LANDING_PAGES],
            },
          ],
        },
        {
          title: "Signals",
          items: [
            {
              text: "Meta",
              path: pathByName(RouteNames.SIGNALS_META_LANDING_PAGES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.SIGNALS_META_LANDING_PAGES],
            },
          ],
        },
        {
          title: "Incrementality",
          items: [
            {
              text: "Tests",
              path: pathByName(RouteNames.INCREMENTALITY_TESTS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.INCREMENTALITY_TESTS],
            },
          ],
        },
        {
          title: "A/B Testing",
          items: [
            {
              text: "Campaigns",
              path: pathByName(RouteNames.AB_TESTING_LAB_PUBLISH, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.AB_TESTING_LAB_PUBLISH],
            },
            {
              text: "Components",
              path: pathByName(RouteNames.COMPONENTS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.COMPONENTS],
            },
            {
              text: "A/B Test Results",
              path: pathByName(RouteNames.AB_TESTING_LAB, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.AB_TESTING_LAB],
            },
            {
              text: "Split Test Results",
              path: pathByName(RouteNames.SPLIT_TESTING_LAB, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.SPLIT_TESTING_LAB],
            },
          ],
          // isBeta: true
        },
        {
          title: "Personalization",
          items: [
            {
              text: "Campaigns",
              path: pathByName(RouteNames.PERSONALIZATION, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.PERSONALIZATION],
            },
            {
              text: "Components",
              path: pathByName(RouteNames.COMPONENTS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.COMPONENTS],
            },
            {
              text: "Results",
              path: pathByName(RouteNames.PERSONALIZATION_REPORT, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.PERSONALIZATION_REPORT],
            },
          ],
          // isBeta: true
        },
      ],
      Report: [
        {
          title: "Tools",
          items: [
            // Only show Chart Builder if user has ANALYTICS_ADMIN role
            ...(hasPermission(ApiACLRole.ANALYTICS_ADMIN)
              ? [
                  {
                    text: "Chart Builder",
                    path: pathByName(RouteNames.CHART_LIBRARY, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.CHART_LIBRARY],
                  },
                ]
              : []),
            {
              text: "Manage Reports",
              path: pathByName(RouteNames.REPORTS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.REPORTS],
            },
          ],
        },
      ],
      Ai: [
        {
          title: "AI",
          items: [
            {
              text: "Chat with your Data",
              path: pathByName(RouteNames.AI_CHAT, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.AI_CHAT],
              action: () => {
                setActive(false);
                history.push(
                  pathByName(RouteNames.AI_CHAT, {
                    clientId: selectedClient?.id,
                    projectId: selectedProject?.id,
                  }),
                );
              },
            },
          ],
        },
      ],
      Settings: [
        {
          title: "Connections",
          items: [
            {
              text: "Integrations",
              path: pathByName(RouteNames.DATA_CONNECTIONS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.DATA_CONNECTIONS],
            },
            {
              text: "Subdomains",
              path: pathByName(RouteNames.SUBDOMAINS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.SUBDOMAINS],
            },
            {
              text: "Data Manager",
              path: pathByName(RouteNames.DATA_MANAGER, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.DATA_MANAGER],
            },
          ],
        },
        {
          title: "Tracking & Attribution",
          items: [
            {
              text: "Attribution Setup",
              path: pathByName(RouteNames.ATTRIBUTION_SETUP, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.ATTRIBUTION_SETUP],
            },
            // b2b only
            ...(hasProjectType(ApiProjectType.TYPE_B2_B)
              ? [
                  {
                    text: "Lead Scoring",
                    path: pathByName(RouteNames.LEAD_SCORING, {
                      clientId: selectedClient?.id,
                      projectId: selectedProject?.id,
                    }),
                    route: routesByName[RouteNames.LEAD_SCORING],
                  },
                ]
              : []),
            {
              text: "Conversions",
              path: pathByName(RouteNames.CONVERSIONS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.CONVERSIONS],
            },
            {
              text: "Effectiveness",
              path: pathByName(RouteNames.EFFECTIVENESS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.EFFECTIVENESS],
            },
          ],
        },
        {
          title: "Traffic & Pages",
          items: [
            {
              text: "Traffic",
              path: pathByName(RouteNames.TRAFFIC_CHANNELS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.TRAFFIC_CHANNELS],
            },
            {
              text: "Locations",
              path: pathByName(RouteNames.LOCATIONS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LOCATIONS],
            },
            {
              text: "Location Tags",
              path: pathByName(RouteNames.LOCATION_TAGS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LOCATION_TAGS],
            },
            // b2b only ???
            {
              text: "Funnel Stages",
              path: pathByName(RouteNames.LEAD_STATUSES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.LEAD_STATUSES],
            },
          ],
        },
        {
          title: "General",
          items: [
            {
              text: "General Settings",
              path: pathByName(RouteNames.SETTINGS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.SETTINGS],
            },
            {
              text: "Alerts",
              path: pathByName(RouteNames.ALERTS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.ALERTS],
            },
          ],
        },
        {
          title: "Audience",
          items: [
            {
              text: "Traits",
              path: pathByName(RouteNames.TRAITS, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.TRAITS],
            },
            {
              text: "Audiences",
              path: pathByName(RouteNames.AUDIENCES, {
                clientId: selectedClient?.id,
                projectId: selectedProject?.id,
              }),
              route: routesByName[RouteNames.AUDIENCES],
            },
          ],
        },
      ],
      SuperAdmin: superAdminSections,
    };
    if (favReports.length > 0 && reportsFetched) {
      const reportsMenu = {
        title: "Favourite Reports",
        items: [] as {
          text: string;
          path: string;
          route: IProtectedRoute;
          action?: () => void;
        }[],
      };
      favReports.forEach((r) => {
        reportsMenu.items.push({
          text: r.name || "",
          path: pathByName(RouteNames.REPORT, {
            clientId: selectedClient?.id,
            projectId: selectedProject?.id,
            reportId: r.id,
          }),
          route: routesByName[RouteNames.REPORT],
        });
      });
      menu.Report.push(reportsMenu);
    }
    if (aiSessions.length > 0 && fetched) {
      const aiMenu = {
        title: "Previous Chats",
        items: [] as IMenuDataItem[],
      };

      const sortedSessions = sortByDate([...aiSessions], "createdAt");
      sortedSessions.forEach((s, index) => {
        const sessionId = s.id;
        if (sessionId) {
          aiMenu.items.push({
            text: s.name || "",
            path: "",
            route: {
              name: RouteNames.AI_CHAT,
            },
            action: () => {
              setActive(true);
              loadSession(sessionId);
            },
            deleteAction: (id: number | string) => {
              handleDeleteSession(id as string);
            },
            id: sessionId,
          });
        }
      });
      menu.Ai.push(aiMenu);
    }
    return menu;
  }, [
    pathByName,
    selectedClient,
    selectedProject,
    favReports,
    reportsFetched,
    hasPermission,
    aiSessions,
    fetched,
    hasProjectType,
    history,
    loadSession,
    routesByName,
    setActive,
  ]);

  // Determine which menu button should be active based on current route
  const activeMenuButton = useMemo(() => {
    if (clickedMenuButton) {
      return clickedMenuButton;
    }
    if (!selectedClient || !selectedProject) {
      if (!hasPermission(ApiACLRole.SUPER_ADMIN)) {
        return null;
      }
      const currentPath = location.pathname;
      const superAdminPaths =
        menuData.SuperAdmin?.flatMap((section) =>
          section.items.map((item) => item.path),
        ) || [];
      if (
        superAdminPaths.some(
          (path) =>
            path && (currentPath === path || currentPath.startsWith(path)),
        )
      ) {
        return "SuperAdmin";
      }
      return null;
    }

    const currentPath = location.pathname;

    // if on project home page or ai is active, show ai
    if (
      currentPath ===
        pathByName(RouteNames.AI_CHAT, {
          clientId: selectedClient?.id,
          projectId: selectedProject?.id,
        }) ||
      active
    ) {
      return "Ai";
    }

    // Check if current path matches any Analyze menu items
    const analyzePaths =
      menuData.Analyze?.flatMap((section) =>
        section.items.map((item) => item.path),
      ) || [];
    if (
      analyzePaths.some(
        (path) => currentPath === path || currentPath.startsWith(path),
      )
    ) {
      return "Analyze";
    }

    // Check if current path matches any Optimize menu items
    const optimizePaths =
      menuData.Optimize?.flatMap((section) =>
        section.items.map((item) => item.path),
      ) || [];
    if (
      optimizePaths.some(
        (path) => currentPath === path || currentPath.startsWith(path),
      )
    ) {
      return "Optimize";
    }

    // Check if current path matches any Report menu items
    const reportPaths =
      menuData.Report?.flatMap((section) =>
        section.items.map((item) => item.path),
      ) || [];
    if (
      reportPaths.some(
        (path) => currentPath === path || currentPath.startsWith(path),
      )
    ) {
      return "Report";
    }

    // Check if current path matches any Settings menu items
    const settingsPaths =
      menuData.Settings?.flatMap((section) =>
        section.items.map((item) => item.path),
      ) || [];

    const trafficPath = pathByName(RouteNames.TRAFFIC, {
      clientId: selectedClient?.id,
      projectId: selectedProject?.id,
    });
    if (
      settingsPaths.some(
        (path) => currentPath === path || currentPath.startsWith(path),
      ) ||
      currentPath.startsWith(trafficPath)
    ) {
      return "Settings";
    }

    // Check if current path matches any SuperAdmin menu items
    const superAdminPaths =
      menuData.SuperAdmin?.flatMap((section) =>
        section.items.map((item) => item.path),
      ) || [];
    if (
      superAdminPaths.some(
        (path) => currentPath === path || currentPath.startsWith(path),
      )
    ) {
      return "SuperAdmin";
    }

    return null;
  }, [
    location.pathname,
    selectedClient,
    selectedProject,
    menuData,
    active,
    pathByName,
    clickedMenuButton,
    hasPermission,
  ]);

  // Use activeMenuButton as default if no hover state
  const currentActiveButton = hoveredButton || activeMenuButton;

  const isSuperAdminMenuOnly =
    (!selectedClient || !selectedProject) &&
    hasPermission(ApiACLRole.SUPER_ADMIN);

  if (isGetStarted) {
    return <></>;
  }

  if (
    (!selectedClient || !selectedProject) &&
    !hasPermission(ApiACLRole.SUPER_ADMIN)
  ) {
    return <></>;
  }

  const pinnedMenuKey =
    currentActiveButton || (isSuperAdminMenuOnly ? "SuperAdmin" : "Analyze");

  return (
    <>
      <SideMenuContainer>
        <TopSection>
          {!isSuperAdminMenuOnly && (
            <>
              <ButtonWithFlyout
                onMouseEnter={() => handleMouseEnter("Analyze")}
                onMouseLeave={handleMouseLeave}
              >
                <MenuButton
                  $isActive={activeMenuButton === "Analyze"}
                  onClick={() => handleMenuButtonClick("Analyze")}
                >
                  <AnalyzeIcon />
                  <ButtonText>Analyze</ButtonText>
                </MenuButton>
                {!isFlyoutPinned &&
                  isFlyoutOpen &&
                  (hoveredButton === "Analyze" ||
                    clickedMenuButton === "Analyze") && (
                    <FlyoutMenuComponent
                      items={menuData.Analyze ?? []}
                      isPinned={isFlyoutPinned}
                      onPinToggle={handlePinToggle}
                      isSecondaryOpen={isSecondaryOpen}
                      isSecondaryAvailable={isSecondaryAvailable}
                      favReports={favReports}
                    />
                  )}
              </ButtonWithFlyout>

              <AccessLevel role={ApiACLRole.PROJECT_OBSERVER} write>
                <ButtonWithFlyout
                  onMouseEnter={() => handleMouseEnter("Optimize")}
                  onMouseLeave={handleMouseLeave}
                >
                  <MenuButton
                    $isActive={activeMenuButton === "Optimize"}
                    onClick={() => handleMenuButtonClick("Optimize")}
                  >
                    <OptimizeIcon />
                    <ButtonText>Optimize</ButtonText>
                  </MenuButton>
                  {!isFlyoutPinned &&
                    isFlyoutOpen &&
                    (hoveredButton === "Optimize" ||
                      clickedMenuButton === "Optimize") && (
                      <FlyoutMenuComponent
                        items={menuData.Optimize ?? []}
                        isPinned={isFlyoutPinned}
                        onPinToggle={handlePinToggle}
                        isSecondaryOpen={isSecondaryOpen}
                        isSecondaryAvailable={isSecondaryAvailable}
                        favReports={favReports}
                      />
                    )}
                </ButtonWithFlyout>
              </AccessLevel>
              <ButtonWithFlyout
                onMouseEnter={() => handleMouseEnter("Report")}
                onMouseLeave={handleMouseLeave}
              >
                <MenuButton
                  $isActive={activeMenuButton === "Report"}
                  onClick={() => handleMenuButtonClick("Report")}
                >
                  <ReportIcon />
                  <ButtonText>Report</ButtonText>
                </MenuButton>
                {!isFlyoutPinned &&
                  isFlyoutOpen &&
                  (hoveredButton === "Report" ||
                    clickedMenuButton === "Report") && (
                    <FlyoutMenuComponent
                      items={menuData.Report ?? []}
                      isPinned={isFlyoutPinned}
                      onPinToggle={handlePinToggle}
                      loading={!reportsFetched}
                      isSecondaryOpen={isSecondaryOpen}
                      isSecondaryAvailable={isSecondaryAvailable}
                      favReports={favReports}
                    />
                  )}
              </ButtonWithFlyout>
              <ButtonWithFlyout
                onMouseEnter={() => handleMouseEnter("Ai")}
                onMouseLeave={handleMouseLeave}
              >
                <MenuButton
                  $isActive={activeMenuButton === "Ai"}
                  onClick={() => handleMenuButtonClick("Ai")}
                >
                  <AiIcon />
                  <ButtonText>AI</ButtonText>
                </MenuButton>
                {!isFlyoutPinned &&
                  isFlyoutOpen &&
                  (hoveredButton === "Ai" || clickedMenuButton === "Ai") && (
                    <FlyoutMenuComponent
                      items={menuData.Ai ?? []}
                      isPinned={isFlyoutPinned}
                      onPinToggle={handlePinToggle}
                      isSecondaryOpen={isSecondaryOpen}
                      isSecondaryAvailable={isSecondaryAvailable}
                      favReports={favReports}
                    />
                  )}
              </ButtonWithFlyout>
            </>
          )}
        </TopSection>

        <BottomSection>
          <AccessLevel role={ApiACLRole.SUPER_ADMIN} write>
            <ButtonWithFlyout
              onMouseEnter={() => handleMouseEnter("SuperAdmin")}
              onMouseLeave={handleMouseLeave}
            >
              <MenuButton
                $isActive={activeMenuButton === "SuperAdmin"}
                onClick={() => handleMenuButtonClick("SuperAdmin")}
              >
                <SuperAdminIcon />
                <ButtonText>Super Admin</ButtonText>
              </MenuButton>
              {!isFlyoutPinned &&
                isFlyoutOpen &&
                (hoveredButton === "SuperAdmin" ||
                  clickedMenuButton === "SuperAdmin") && (
                  <FlyoutMenuComponent
                    items={menuData.SuperAdmin ?? []}
                    isPinned={isFlyoutPinned}
                    onPinToggle={handlePinToggle}
                    isSecondaryOpen={isSecondaryOpen}
                    isSecondaryAvailable={isSecondaryAvailable}
                    favReports={favReports}
                  />
                )}
            </ButtonWithFlyout>
          </AccessLevel>
          {!isSuperAdminMenuOnly && (
            <AccessLevel role={ApiACLRole.PROJECT_OBSERVER} write>
              <ButtonWithFlyout
                onMouseEnter={() => handleMouseEnter("Settings")}
                onMouseLeave={handleMouseLeave}
              >
                <MenuButton
                  $isActive={activeMenuButton === "Settings"}
                  onClick={() => handleMenuButtonClick("Settings")}
                >
                  <SettingsIcon />
                  <ButtonText>Settings</ButtonText>
                </MenuButton>
                {!isFlyoutPinned &&
                  isFlyoutOpen &&
                  (hoveredButton === "Settings" ||
                    clickedMenuButton === "Settings") && (
                    <FlyoutMenuComponent
                      items={menuData.Settings ?? []}
                      isPinned={isFlyoutPinned}
                      onPinToggle={handlePinToggle}
                      isSecondaryOpen={isSecondaryOpen}
                      isSecondaryAvailable={isSecondaryAvailable}
                      favReports={favReports}
                    />
                  )}
              </ButtonWithFlyout>
            </AccessLevel>
          )}
        </BottomSection>
      </SideMenuContainer>
      {isFlyoutPinned && (
        <FlyoutMenuComponent
          items={menuData[pinnedMenuKey as keyof typeof menuData] ?? []}
          isPinned={true}
          onPinToggle={handlePinToggle}
          isSecondaryOpen={isSecondaryOpen}
          isSecondaryAvailable={isSecondaryAvailable}
          favReports={favReports}
        />
      )}
    </>
  );
}

export default SideMenu;
